import { Box } from "@mui/material";
import React, { Suspense, lazy } from "react";

import type { EntityDetailMode } from "../../types/entityRegistry";
import { ProductDetail } from "../../types/productPlane";
import ClaimTable from "../Detail/ClaimTable";
import { DetailParts, DetailSection, hasContent } from "../Detail/DetailSections";
import SourceTable from "../Detail/SourceTable";
import SpecificationTable from "../Detail/SpecificationTable";
import { specificationModel } from "../Detail/specificationModel";
import OpenInValueChainLink from "../KindDetail/OpenInValueChainLink";
import ProductOrganizations, {
  countOrganizations,
} from "./ProductOrganizations";
import ProductEvidence from "./ProductEvidence";
import ProductOverview, { hasProductOverview } from "./ProductOverview";
import ProductRelationTable, {
  ProductRelationTableProps,
} from "./ProductRelationTable";
import ProductStandards from "./ProductStandards";
import ProductTimeline from "./ProductTimeline";
import { facilityMapModel } from "./facilityMapModel";
import ComponentTable from "./ComponentTable";
import {
  RelationRow,
  componentRows,
  familyRows,
  hostRows,
  linkRows,
  otherLinkRows,
  payloadRows,
} from "./productRelations";
import { timelineModel } from "./timelineModel";

// The map of the facilities, loaded with the map library only for a product
// that has a facility to place
const FacilityMap = lazy(() => import("./FacilityMap"));

// The height (px) the map stands at, kept while it loads: the map and its
// legend beneath
const FACILITY_MAP_HEIGHT = 620;

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

// A section that gathers several parts, each under its own heading; left
// out where no part has anything to show
const gathered = (id: string, label: string, parts: DetailSection[]): DetailSection => ({
  id,
  label,
  content: parts.some(hasContent) && <DetailParts parts={parts} />,
});

/**
 * The sections of the product view, in the order shown: a few broad ones,
 * some gathering several parts (the claims, specifications and standards
 * of its capabilities; the timeline, programmes and incidents of its
 * activity). A section or part the product has nothing in has no content,
 * and so is left out of the view.
 * For a printed page (`print`) the claims and specifications are plain
 * tables, closed on their quotes and without sources, and the source list
 * is left out.
 */
export const productSections = (
  product: ProductDetail,
  mode: EntityDetailMode,
  print = false,
): DetailSection[] => {
  const organizations = countOrganizations(product.organizations);

  const specifications = specificationModel(product.specifications);
  const specificationCount = specifications.curated.length + specifications.other.length;

  const components = componentRows(product.structure.contains);
  const payloads = payloadRows(product.structure.contains, product.structure.other);
  // An unnamed row counts each component it stands for
  const componentCount = components.reduce((count, row) => count + (row.count ?? 1), 0);
  // A part of the composition with a plain table of its rows
  const compositionPart = (
    id: string,
    label: string,
    rows: RelationRow[],
    detailHeading: string,
  ): DetailSection => ({
    id,
    label,
    count: countOf(rows.length),
    content: rows.length > 0 && (
      <ProductRelationTable
        rows={rows}
        entityHeading="Product"
        columns={["category", "detail", "latest"]}
        headings={{ detail: detailHeading }}
        print={print}
      />
    ),
  });
  const compositionParts: DetailSection[] = [
    {
      id: "components",
      label: "Components",
      count: countOf(componentCount),
      content: components.length > 0 && <ComponentTable rows={components} print={print} />,
    },
    compositionPart("payloads", "Payloads", payloads, "Role"),
    compositionPart(
      "used-in",
      "Used in",
      hostRows(product.structure.built_into, product.typical_hosts, product.structure.other),
      "Function",
    ),
    compositionPart(
      "family",
      "Family",
      familyRows(product.structure.variant_of, product.structure.variants),
      "Context",
    ),
    compositionPart("other-links", "Other links", otherLinkRows(product.structure.other), "Detail"),
  ];

  // The programmes and incidents on one axis of time; not on a printed page
  const timeline = print
    ? null
    : timelineModel(linkRows(product.projects), linkRows(product.incidents), Date.now());
  const timelineCount = timeline
    ? timeline.programmes.length +
      timeline.lanes.reduce((count, lane) => count + lane.marks.length, 0)
    : 0;

  // A section of linked entities of one kind: their table, counted in the
  // strip
  const linkSection = (
    id: string,
    label: string,
    rows: RelationRow[],
    table: Omit<ProductRelationTableProps, "rows" | "print">,
    // What stands above the table, such as the map of the facilities
    above?: React.ReactNode,
  ): DetailSection => ({
    id,
    label,
    count: countOf(rows.length),
    content: rows.length > 0 && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {above}
        <ProductRelationTable rows={rows} print={print} {...table} />
      </Box>
    ),
  });

  // The facilities on a map; not on a printed page
  const facilities = linkRows(product.facilities);
  const facilityMap = print ? null : facilityMapModel(facilities);

  return [
    // How much evidence there is, then what the product is
    {
      id: "overview",
      label: "Overview",
      hideHeading: true,
      content: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <ProductEvidence product={product} print={print} />
          {hasProductOverview(product) && <ProductOverview product={product} />}
        </Box>
      ),
    },
    {
      id: "organizations",
      label: "Organizations",
      count: countOf(organizations),
      content: organizations > 0 && (
        <ProductOrganizations groups={product.organizations} />
      ),
    },
    gathered("capabilities", "Capabilities", [
      {
        id: "claims",
        label: "Claims",
        heading: "Capability claims",
        count: countOf(product.claims.length),
        content: product.claims.length > 0 && (
          <ClaimTable claims={product.claims} print={print} />
        ),
      },
      {
        id: "specifications",
        label: "Specifications",
        // The metrics the table shows, not the ones it leaves out
        count: countOf(specificationCount),
        // Left out where every specification is left out of the table
        content: specificationCount > 0 && (
          <SpecificationTable
            specifications={product.specifications}
            print={print}
          />
        ),
      },
      {
        id: "standards",
        label: "Standards",
        count: countOf(product.standards.length),
        content: product.standards.length > 0 && (
          <ProductStandards standards={product.standards} print={print} />
        ),
      },
    ]),
    // Its components, what it is built into, its family of models and the
    // systems it otherwise works with; beneath them the step into the whole
    // chain, rooted at the product's class as in the head
    {
      id: "composition",
      label: "Composition",
      content: compositionParts.some(hasContent) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DetailParts parts={compositionParts} />
          {!print && product.categories.length > 0 && (
            <Box>
              <OpenInValueChainLink
                root={{ type: "class", id: product.categories[0].category.id }}
              />
            </Box>
          )}
        </Box>
      ),
    },
    gathered("activity", "Activity", [
      {
        id: "timeline",
        label: "Timeline",
        count: countOf(timelineCount),
        content: timeline && <ProductTimeline model={timeline} />,
      },
      linkSection("programmes", "Programmes", linkRows(product.projects), {
        entityHeading: "Programme",
        columns: ["status", "period", "countries", "quantity", "latest"],
        filterBy: "type",
      }),
      linkSection("incidents", "Incidents", linkRows(product.incidents), {
        entityHeading: "Incident",
        columns: ["severity", "date", "countries", "quantity", "latest"],
        filterBy: "type",
      }),
    ]),
    linkSection(
      "facilities",
      "Facilities",
      facilities,
      {
        entityHeading: "Facility",
        columns: ["status", "address", "countries", "period", "quantity", "latest"],
        headings: { period: "Operating" },
      },
      facilityMap && (
        <Suspense fallback={<Box sx={{ height: FACILITY_MAP_HEIGHT }} />}>
          <FacilityMap model={facilityMap} />
        </Suspense>
      ),
    ),
    {
      id: "sources",
      label: "Sources",
      count: countOf(product.source_count),
      content: !print && product.sources.items.length > 0 && (
        <SourceTable
          sources={product.sources.items}
          total={product.sources.total}
          mode={mode}
        />
      ),
    },
  ];
};
